import { useState } from 'react';
import type { AgentRole, WorkflowPhase } from '../lib/api';
import { getPhaseColor } from '../lib/utils';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { MarkdownEditor } from './MarkdownEditor';

interface Props {
  agent: AgentRole;
  workflowPhases?: WorkflowPhase[];
  onSave: (agent: AgentRole) => void;
  onClose: () => void;
}

export function AgentRoleEditor({ agent, workflowPhases, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<AgentRole>({ ...agent, phases: [...agent.phases], capabilities: [...agent.capabilities], settings: { ...agent.settings, tools: [...agent.settings.tools] } });
  const [capInput, setCapInput] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEscapeKey(onClose);

  const update = <K extends keyof AgentRole>(key: K, value: AgentRole[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const togglePhase = (phase: string) => {
    setDraft((prev) => ({
      ...prev,
      phases: prev.phases.includes(phase) ? prev.phases.filter((p) => p !== phase) : [...prev.phases, phase],
    }));
  };

  const addCapability = () => {
    const cap = capInput.trim();
    if (cap && !draft.capabilities.includes(cap)) {
      setDraft((prev) => ({ ...prev, capabilities: [...prev.capabilities, cap] }));
      setCapInput('');
    }
  };

  const removeCapability = (cap: string) => {
    setDraft((prev) => ({ ...prev, capabilities: prev.capabilities.filter((c) => c !== cap) }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    onSave(draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 h-[90vh] w-[90vw] max-w-6xl overflow-y-auto rounded-xl border border-gray-800 bg-surface-850 p-6 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">Edit Agent Role</h2>

        <div className="space-y-5">
          {/* Basic */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Name</label>
              <input
                value={draft.name}
                onChange={(e) => update('name', e.target.value)}
                className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                autoFocus
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Type</label>
              <input
                type="text"
                value={draft.type}
                onChange={(e) => update('type', e.target.value)}
                className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
                placeholder="e.g. builder, triager, reviewer"
              />
            </div>
          </div>

          {/* System Prompt */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">System Prompt</label>
            <MarkdownEditor
              value={draft.systemPrompt}
              onChange={(val) => update('systemPrompt', val)}
              placeholder="Instructions for this agent..."
              maxLength={10000}
            />
          </div>

          {/* Capabilities */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">Capabilities</label>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {draft.capabilities.map((cap) => (
                <span
                  key={cap}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-800 px-2.5 py-1 text-xs text-gray-300"
                >
                  {cap}
                  <button
                    type="button"
                    onClick={() => removeCapability(cap)}
                    className="text-gray-500 hover:text-gray-300"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={capInput}
                onChange={(e) => setCapInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCapability();
                  }
                }}
                className="flex-1 rounded-lg border border-gray-700 bg-surface-900 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                placeholder="Add capability..."
              />
              <button
                type="button"
                onClick={addCapability}
                className="rounded-lg bg-gray-800 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700"
              >
                Add
              </button>
            </div>
          </div>

          {/* Phases */}
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Phases</label>
            {workflowPhases && workflowPhases.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {workflowPhases.map((phase) => {
                  const active = draft.phases.includes(phase.name);
                  const color = getPhaseColor(phase.color).badge;
                  return (
                    <button
                      key={phase.name}
                      type="button"
                      onClick={() => togglePhase(phase.name)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${
                        active
                          ? `${color} border-transparent`
                          : 'border-gray-700 bg-surface-900 text-gray-500 hover:border-gray-600'
                      }`}
                    >
                      {phase.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">Add workflow phases first</p>
            )}
          </div>

          {/* Advanced Settings */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-300"
            >
              <svg
                className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              Advanced Settings
            </button>

            {showAdvanced && (
              <div className="mt-3 space-y-3 rounded-lg border border-gray-800 bg-surface-900 p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[10px] text-gray-500">Model</label>
                    <input
                      value={draft.settings.model}
                      onChange={(e) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, model: e.target.value } }))}
                      className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] text-gray-500">Context Window</label>
                    <input
                      type="number"
                      value={draft.settings.contextWindow}
                      onChange={(e) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, contextWindow: Number(e.target.value) } }))}
                      min={1000}
                      max={200000}
                      step={1000}
                      className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 flex items-center justify-between text-[10px] text-gray-500">
                      <span>Temperature</span>
                      <span>{draft.settings.temperature.toFixed(1)}</span>
                    </label>
                    <input
                      type="range"
                      value={draft.settings.temperature}
                      onChange={(e) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, temperature: Number(e.target.value) } }))}
                      min={0}
                      max={2}
                      step={0.1}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[10px] text-gray-500">Max Retries</label>
                    <input
                      type="number"
                      value={draft.settings.maxRetries}
                      onChange={(e) => setDraft((prev) => ({ ...prev, settings: { ...prev.settings, maxRetries: Number(e.target.value) } }))}
                      min={0}
                      max={10}
                      className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[10px] text-gray-500">Tools (comma-separated)</label>
                  <input
                    value={draft.settings.tools.join(', ')}
                    onChange={(e) => setDraft((prev) => ({
                      ...prev,
                      settings: { ...prev.settings, tools: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) },
                    }))}
                    className="w-full rounded-lg border border-gray-700 bg-surface-850 px-3 py-1.5 text-xs text-gray-100 outline-none focus:border-primary-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!draft.name.trim()}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
          >
            Save Agent
          </button>
        </div>
      </form>
    </div>
  );
}
